import { useEffect, useMemo, useState } from 'react';
import { failedJobs, ping, reprintFailed } from '../lib/bridge.ts';
import { logout } from '../lib/api.ts';
import { thaiDate } from '../lib/format.ts';
import { useBill } from '../bill.tsx';
import { useStore } from '../store.tsx';
import { Alert, Modal, useToast } from '../ui.tsx';
import { BillGrid } from './BillGrid.tsx';
import { CartPanel } from './CartPanel.tsx';
import { NewBillModal } from './NewBillModal.tsx';
import { PaymentModal } from './PaymentModal.tsx';
import { ProductGrid } from './ProductGrid.tsx';
import { ShortcutPad } from './ShortcutPad.tsx';
import { TableGrid } from './TableGrid.tsx';

/**
 * หน้าจอขาย
 *
 * ★ ลอกโครงจาก screens/MainScreen.js ★
 *
 * ทั้งแอปเดิมเป็น "หน้าเดียว" ที่สลับเนื้อด้วยแถบหมวดด้านล่าง
 * โดยมีสองปุ่มพิเศษปนอยู่ในแถบเดียวกับหมวดสินค้า
 *
 *   BILL   ซ้ายสุด  — รายการบิลที่เปิดค้าง
 *   หมวด…            — กริดสินค้าของหมวดนั้น (กลุ่ม 99 อยู่ท้ายสุดของหมวด)
 *   TABLE  ขวาสุด   — ผังโต๊ะ
 *
 * ★ ไม่ใช่หน้าแยกกันคนละหน้า ★ นี่คือจุดที่ทำให้จำนวนคลิกของพนักงานเท่าเดิม
 * เลือกโต๊ะแล้วกระโดดเข้าหมวดสินค้าแรกทันที ไม่มีขั้นกลาง
 */

const BILL_KEY = '__bill__';
const TABLE_KEY = '__table__';

export function PosShell({ onDrawer }: { onDrawer: () => void }) {
  const store = useStore();
  const bill = useBill();
  const toast = useToast();

  const [railKey, setRailKey] = useState<string>(BILL_KEY);
  const [search, setSearch] = useState('');
  const [showShortcut, setShowShortcut] = useState(false);
  const [deleteMode, setDeleteMode] = useState(false);
  const [showNewBill, setShowNewBill] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [bridgeOk, setBridgeOk] = useState<boolean | null>(null);
  /** ★ ชำระเงินเป็นกล่องซ้อน ไม่ใช่เปลี่ยนหน้า ★ พนักงานยังเห็นรายการอาหารข้างหลัง */
  const [payingSaleId, setPayingSaleId] = useState<number | null>(null);

  /** ★ ลำดับปุ่มบนแถบล่าง ★ Bill → หมวดสินค้า → Table */
  const rail = useMemo(
    () => [
      { key: BILL_KEY, name: 'Bill', image: '/ui/bill.png', groupId: null },
      ...store.productGroups.map((g) => ({
        key: String(g.id), name: g.name, image: g.imageUrl, groupId: g.id,
      })),
      { key: TABLE_KEY, name: 'Table', image: '/ui/table.png', groupId: null },
    ],
    [store.productGroups],
  );

  /** หมวดสินค้าหมวดแรก — ที่ที่เด้งไปหลังเลือกโต๊ะหรือยืนยันประเภทบิล */
  const firstGroupKey = store.productGroups[0] ? String(store.productGroups[0].id) : BILL_KEY;

  // เปลี่ยนหมวดแล้วล้างคำค้นและปิดทางลัด เหมือนของเดิม
  useEffect(() => {
    setSearch('');
    setShowShortcut(false);
  }, [railKey]);

  useEffect(() => {
    let alive = true;
    const check = async () => {
      const result = await ping();
      if (alive) setBridgeOk(result.ok);
    };
    void check();
    const timer = setInterval(check, 30_000);
    return () => { alive = false; clearInterval(timer); };
  }, []);

  const onBillTab = railKey === BILL_KEY;
  const onTableTab = railKey === TABLE_KEY;
  const onProductTab = !onBillTab && !onTableTab;
  const activeGroupId = onProductTab ? Number(railKey) : null;

  const title = onBillTab
    ? 'รายการบิล'
    : onTableTab
      ? 'เลือกโต๊ะ'
      : (store.productGroups.find((g) => String(g.id) === railKey)?.name ?? '-');

  const pending = failedJobs();
  const isDelivery = bill.payload.billType === 'delivery';

  const whereText = () => {
    if (bill.payload.billType === 'dine_in') return `โต๊ะ ${bill.payload.tableName || '-'}`;
    if (bill.payload.billType === 'takeaway') return bill.payload.customerName || 'กลับบ้าน';
    if (bill.payload.billType === 'delivery') {
      return `${bill.payload.deliveryName || 'เดลิเวอรี่'}${
        bill.payload.deliveryOrderRef ? ` : ${bill.payload.deliveryOrderRef}` : ''
      }`;
    }
    return '';
  };

  return (
    <div className="pos">
      {/* ══ หัวจอ ══ */}
      <div className="pos-head">
        <button className="head-menu" onClick={() => setShowMenu(true)}>☰</button>
        <span className="title">{title}</span>

        {onProductTab && (
          <>
            <span className="spacer" />
            <div className="head-search">
              <input
                value={search}
                onChange={(e) => {
                  setShowShortcut(false);
                  setSearch(e.target.value);
                }}
                placeholder="ค้นหา"
              />
              <button className="clear" onClick={() => setSearch('')}>✕</button>
            </div>
            <button
              className="head-btn"
              aria-pressed={showShortcut}
              onClick={() => setShowShortcut((s) => !s)}
            >
              ทางลัด
            </button>
          </>
        )}

        {onBillTab && (
          <>
            <span className="spacer" />
            {pending.length > 0 && (
              <span className="chip crit">พิมพ์ไม่ออก {pending.length}</span>
            )}
            <span className={`chip ${bridgeOk === false ? 'crit' : ''}`}>
              {bridgeOk === null ? 'ตรวจเครื่องพิมพ์' : bridgeOk ? 'เครื่องพิมพ์พร้อม' : 'เครื่องพิมพ์ไม่ตอบ'}
            </span>
            <button
              className="head-btn"
              aria-pressed={deleteMode}
              onClick={() => setDeleteMode((d) => !d)}
              title="โหมดยกเลิกบิล"
            >
              🗑
            </button>
          </>
        )}

        {onTableTab && <span className="spacer" />}

        {onProductTab && (
          <div className="head-bill">
            <div className="price-mode">
              <span className="label">ราคา {isDelivery ? 'เดลิเวอรี่' : 'ปกติ'}</span>
              <button onClick={() => setShowNewBill(true)}>เปลี่ยนแปลง</button>
            </div>
            <span className="where">{whereText()}</span>
            <span className="guests">
              {bill.payload.billType === 'dine_in' && <>👤 {bill.payload.guestCount}</>}
              {bill.payload.billType === 'takeaway' && bill.payload.customerPhone}
            </span>
          </div>
        )}
      </div>

      {/* ══ เนื้อ ══ */}
      <div className="pos-body">
        <div className="pos-main">
          {onBillTab && (
            <BillGrid
              deleteMode={deleteMode}
              onNewBill={() => {
                if (!store.drawer) {
                  toast('รอบลิ้นชักยังไม่เปิด', 'warn');
                  return;
                }
                setShowNewBill(true);
              }}
              onOpened={() => setRailKey(firstGroupKey)}
            />
          )}
          {onTableTab && <TableGrid onPicked={() => setRailKey(firstGroupKey)} />}
          {onProductTab &&
            (showShortcut ? (
              <ShortcutPad />
            ) : (
              <ProductGrid groupId={activeGroupId} searchText={search} />
            ))}
        </div>

        {/* ตะกร้าโผล่เฉพาะตอนอยู่หมวดสินค้า เหมือนของเดิม */}
        {onProductTab && (
          <CartPanel
            onPay={() => { if (bill.payload.saleId !== null) setPayingSaleId(bill.payload.saleId); }}
            onSaved={() => setRailKey(BILL_KEY)}
          />
        )}
      </div>

      {/* ══ แถบหมวดล่าง ══ */}
      <div className="pos-rail">
        {rail.map((item) => (
          <button
            key={item.key}
            className="rail-item"
            aria-pressed={railKey === item.key}
            onClick={() => setRailKey(item.key)}
          >
            {item.image ? (
              <img src={item.image} alt="" />
            ) : (
              <span className="rail-icon">🍽</span>
            )}
            <span>{item.name}</span>
          </button>
        ))}
      </div>

      {payingSaleId !== null && (
        <PaymentModal
          saleId={payingSaleId}
          onClose={() => setPayingSaleId(null)}
          onPaid={() => {
            setPayingSaleId(null);
            bill.reset();
            setRailKey(BILL_KEY);
          }}
        />
      )}

      {showNewBill && (
        <NewBillModal
          onClose={() => setShowNewBill(false)}
          onConfirmed={(billType) => {
            setShowNewBill(false);
            // ทานที่ร้านต้องเลือกโต๊ะก่อน ประเภทอื่นเข้าหมวดสินค้าได้เลย
            setRailKey(billType === 'dine_in' ? TABLE_KEY : firstGroupKey);
          }}
        />
      )}

      {showMenu && (
        <Modal title="เมนู" onClose={() => setShowMenu(false)}>
          <div className="rowitem">
            <span className="grow">
              <strong>{store.session.user.displayName}</strong>
              <div className="sub">
                {store.session.user.role === 'owner' ? 'เจ้าของร้าน' : 'พนักงาน'} ·{' '}
                {store.branch.name}
                {store.drawer && ` · วันทำการ ${thaiDate(store.drawer.businessDate)}`}
              </div>
              {/* ขนาดจอ — ใช้ตอนปรับหน้าจอให้พอดีเครื่องแต่ละรุ่น */}
              <div className="sub" style={{ fontFamily: 'var(--mono)' }}>
                จอ {window.innerWidth}×{window.innerHeight}
                {document.fullscreenElement ? ' · เต็มจอ' : ''}
              </div>
            </span>
          </div>

          {!store.drawer && <Alert tone="warn">ยังไม่ได้เปิดรอบลิ้นชักของวันนี้</Alert>}

          {pending.length > 0 && (
            <>
              <h2>งานพิมพ์ที่ไม่ออก</h2>
              <p className="lede">
                ออเดอร์และบิลบันทึกไว้เรียบร้อยแล้ว ที่ค้างอยู่คือกระดาษเท่านั้น
              </p>
              <div className="rowlist">
                {pending.map((job) => (
                  <div key={job.id} className="rowitem">
                    <span className="grow">{job.label}</span>
                    <button
                      className="btn ghost"
                      style={{ minHeight: 40 }}
                      onClick={async () => {
                        const result = await reprintFailed(job);
                        toast(result.ok ? 'พิมพ์แล้ว' : (result.message ?? 'ยังพิมพ์ไม่ได้'),
                              result.ok ? 'ok' : 'crit');
                      }}
                    >
                      พิมพ์ซ้ำ
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}

          {/*
            ★ เต็มจอ ★ ซ่อนแถบที่อยู่เว็บของเบราว์เซอร์
            ได้พื้นที่จอคืนราว 10% และพนักงานไม่เผลอกดออกจากแอประหว่างขาย

            ใช้ได้ทันทีแม้เปิดผ่าน http ธรรมดา ต่างจากการติดตั้งเป็นแอป
            ซึ่งต้องมี HTTPS ก่อน (ดู README)
          */}
          <div className="action-row" style={{ marginTop: 18 }}>
            <button
              className="btn"
              onClick={async () => {
                try {
                  if (document.fullscreenElement) await document.exitFullscreen();
                  else await document.documentElement.requestFullscreen({ navigationUI: 'hide' });
                  setShowMenu(false);
                } catch {
                  toast('เบราว์เซอร์นี้ไม่ยอมให้เข้าโหมดเต็มจอ', 'warn');
                }
              }}
            >
              {document.fullscreenElement ? 'ออกจากเต็มจอ' : 'เต็มจอ'}
            </button>
            <button className="btn ghost" onClick={() => { setShowMenu(false); onDrawer(); }}>
              ลิ้นชัก
            </button>
          </div>

          <div className="action-row">
            <button className="btn ghost" onClick={() => void store.reload()}>
              โหลดข้อมูลใหม่
            </button>
            <button className="btn danger" onClick={logout}>ออกจากระบบ</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
